import {Button, Form, Input, Modal, Select} from "antd";
import React, {useEffect, useState} from "react";
import {useNavigate, useParams} from "react-router";
import {useAppDispatch, useAppSelector} from "../app/hooks";
import {RootState} from "../app/store";
import {ConfirmDialog} from "../components/ConfirmDialog";
import {PageHeader} from "../components/PageHeader";
import {SelectPersons} from "../components/SelectPersons";
import {personColumns} from "../components/personColumns";
import {TableSkeleton} from "../components/TableSkeleton";
import {fetchDoctorAvaliableCaretakers} from "../features/caretakersToAssign/caretakersToAssignSlice";
import {
    addCaretakersToDoctor,
    addPatientsToDoctor,
    clearSaveDoctorError,
    createOrUpdateDoctor, deleteDoctor,
    fetchDoctor,
    fetchDoctorCaretakers,
    fetchDoctorPatients,
    initDoctorState,
    removeCaretakersFromDoctor,
    removePatientsFromDoctor, updateDoctorFields
} from "../features/doctor/doctorSlice";
import {detachDoctor, fetchDoctors} from "../features/doctor/doctorsSlice";
import {fetchDoctorAvaliablePatients} from "../features/patientsToAssign/patientsToAssignSlice";
import {Doctor} from "../model/doctor";
import {Person} from "../model/person";
import {RouteNames} from "../routes";
import {useTranslate} from "../i18n";
import {getErrorMessage} from "../utils/getErrorMessage";
import {AssignCaretakersToDoctor} from "./AssignCaretakersToDoctor";
import {AssignPatientsToDoctor} from "./AssignPatientsToDoctor";
import {DoctorRequestDto} from "../api/client-axios";
import {
    ArrowLeftOutlined,
    CheckOutlined,
    CloseOutlined,
    DeleteOutlined,
    ScissorOutlined
} from "@ant-design/icons";

const {Option} = Select;

export const EditDoctor = () => {
    const params = useParams();
    const navigate = useNavigate();
    const dispatch = useAppDispatch();
    const t = useTranslate();
    const doctorsStatus = useAppSelector((state: RootState) => state.doctors.status);
    const doctor = useAppSelector((state: RootState) => state.doctor.doctor);
    const status = useAppSelector((state: RootState) => state.doctor.status);
    const error = useAppSelector((state: RootState) => state.doctor.error);
    const doctorPatients = useAppSelector((state: RootState) => state.doctor.patients);
    const doctorCaretakers = useAppSelector((state: RootState) => state.doctor.caretakers);
    const countries = useAppSelector((state: RootState) => state.country.countries);

    const doctorId = params.doctorId;
    const isNew = doctorId === undefined || doctorId === "new";
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [isRemoveDoctorWarningVisible, setIsRemoveDoctorWarningVisible] = useState(false);
    const [isDeleteDoctorWarningVisible, setIsDeleteDoctorWarningVisible] = useState(false);

    const [tempDoctor, setTempDoctor] = useState<Doctor>()

    useEffect(() => {

        if (doctorsStatus === "idle") {
            dispatch(fetchDoctors())
        }
        dispatch(fetchDoctor(doctorId!))
        if (doctorId !== "new") {
            dispatch(fetchDoctorPatients(doctorId!));
            dispatch(fetchDoctorCaretakers(doctorId!));
        }


    }, []);

    useEffect(() => {
        if (status === "saved") {
            dispatch(fetchDoctors());
            dispatch(initDoctorState());
            navigate(`/${RouteNames.doctors}`);
        }
        if (status === "auth-error" || status === "error") {
            setIsModalVisible(true);
        }
    }, [status])


    const onCancel = () => {
        navigate(`/${RouteNames.doctors}`);
    }

    const onSave = () => {
        if (doctor == undefined) {
            return;
        }
        setTempDoctor(doctor as Doctor);
        dispatch(createOrUpdateDoctor(doctor as DoctorRequestDto));
    }


    const onDelete = () => {
        setIsDeleteDoctorWarningVisible(true);
    }

    const onRemove = () => {
        setIsRemoveDoctorWarningVisible(true);
    }

    const onDeleteDoctor = () => {
        if (doctor?.id != null) {
            dispatch(deleteDoctor(doctor.id));
        }
    }

    const onRemoveDoctor = () => {
        if (doctor?.id != null) {
            dispatch(detachDoctor(doctor.id));
        }
    }

    const handleAuthOk = () => {
        setIsModalVisible(false)
        navigate("/")
    }

    const handleErrorOk = () => {
        setIsModalVisible(false);
        dispatch(clearSaveDoctorError(tempDoctor!));
    }

    const handleCancel = () => {
        setIsModalVisible(false)
    }

    const reloadDoctorPatients = () => {
        dispatch(fetchDoctorPatients(doctorId!));
    }

    const reloadDoctorCaretakers = () => {
        dispatch(fetchDoctorCaretakers(doctorId!));
    }

    const onPatientsRemove = (selectedPersons: Person[] | undefined) => {
        if (selectedPersons === undefined || selectedPersons.length === 0) return;
        dispatch(removePatientsFromDoctor({doctorId: doctorId!, patients: selectedPersons.map(p => p.id)}));
    }

    const onCaretakersRemove = (selectedPersons: Person[] | undefined) => {
        if (selectedPersons === undefined || selectedPersons.length === 0) return;
        dispatch(removeCaretakersFromDoctor({doctorId: doctorId!, caretakersIds: selectedPersons.map(p => p.id)}));
    }

    const onPatientsAssign = () => {
        dispatch(fetchDoctorAvaliablePatients(doctorId!));
        dispatch(addPatientsToDoctor());
    }

    const onCaretakersAssign = () => {
        dispatch(fetchDoctorAvaliableCaretakers(doctorId!));
        dispatch(addCaretakersToDoctor());
    }

    const changeDoctor = (doctor: DoctorRequestDto) => {
        dispatch(updateDoctorFields(doctor));
    }

    const columns = personColumns(t);

    const doctorName = [doctor?.firstName, doctor?.lastName].filter(Boolean).join(" ");


    if (status === "idle" || status === "loading" || doctorsStatus === "idle") {
        return <>
            <PageHeader eyebrow={t("nav.doctors")} title={t(isNew ? "doctor.new" : "doctor.one")}/>
            <TableSkeleton/>
        </>;
    }

    if (status === "auth-error") {
        return <Modal
            title={t("auth.failed.title")}
            visible={isModalVisible}
            onOk={handleAuthOk}
            okText={t("common.signIn")}
            onCancel={handleCancel}
            cancelButtonProps={{hidden: true}}
        >
            <p>{t("auth.failed.body")}</p>
        </Modal>
    }

    if (status === "error") {
        const errorMessage = getErrorMessage(error, t);
        return <Modal
            title={errorMessage.title}
            visible={isModalVisible}
            onOk={handleErrorOk}
            onCancel={handleCancel}
            cancelButtonProps={{hidden: true}}
        >
            <p>{errorMessage.details}</p>
        </Modal>
    }

    if (status === "saved") {
        return (<div></div>);
    }


    if (status == "addingPatients") {
        return <AssignPatientsToDoctor doctorId={doctorId!}/>
    }

    if (status == "addingCaretakers") {
        return <AssignCaretakersToDoctor doctorId={doctorId!}/>
    }

    const editDoctor = <div className="edit-person-main-frame">
        <Form className='person-form'
              name="basic"
              labelCol={{span: 7}}
              wrapperCol={{span: 17}}
              initialValues={{remember: true}}
              autoComplete="off"
        >
            <div className="person-form__actions">
                <Button className="btn-warning" icon={<ScissorOutlined/>} onClick={onRemove}
                        disabled={doctor?.id === undefined}>{t("common.remove")}</Button>
                <Button danger icon={<DeleteOutlined/>} onClick={onDelete}
                        disabled={doctor?.id === undefined}>{t("common.delete")}</Button>
                <Button icon={<CloseOutlined/>} onClick={onCancel}>{t("common.cancel")}</Button>
                <Button icon={<CheckOutlined/>} type='primary' onClick={onSave}>{t("common.save")}</Button>
            </div>

            <Form.Item label={t("person.field.email")}>
                <Input placeholder={t("person.placeholder.email")}
                       disabled={!isNew}
                       defaultValue={doctor?.userAccount?.emailAddress || ""}
                       onChange={(e) => {
                           changeDoctor({
                               ...doctor,
                               userAccount: {...doctor?.userAccount, emailAddress: e.target.value}
                           } as DoctorRequestDto)
                       }}
                />
            </Form.Item>
            {isNew
                ? <Form.Item label={t("person.field.password")}>
                    <Input.Password placeholder={t("person.placeholder.password")} onChange={(e) => {
                        changeDoctor({
                            ...doctor,
                            password: e.target.value
                        } as DoctorRequestDto)
                    }}/>
                </Form.Item>
                : null}
            <Form.Item label={t("person.field.phone")} rules={[{required: true}]}>
                <Input placeholder={t("person.placeholder.phone")} defaultValue={doctor?.userAccount?.mobileNumber || ""}
                       onChange={(e) => {
                           changeDoctor({
                               ...doctor,
                               userAccount: {...doctor?.userAccount, mobileNumber: e.target.value}
                           } as DoctorRequestDto)
                       }}
                />
            </Form.Item>
            <Form.Item label={t("person.field.firstName")} rules={[{required: true, message: t("person.rule.firstName")}]}>
                <Input placeholder={t("person.placeholder.firstName")} defaultValue={doctor?.firstName || ""} onChange={(e) => {
                    changeDoctor({...doctor, firstName: e.target.value} as DoctorRequestDto)
                }}/>
            </Form.Item>
            <Form.Item label={t("person.field.lastName")}>
                <Input placeholder={t("person.placeholder.lastName")} defaultValue={doctor?.lastName || ""} onChange={(e) => {
                    changeDoctor({...doctor, lastName: e.target.value} as DoctorRequestDto)
                }}/>
            </Form.Item>
            <Form.Item label={t("person.field.country")} initialValue={doctor?.address?.country}>
                <Select
                    onChange={(e) => changeDoctor({
                        ...doctor,
                        address: {...doctor?.address, alpha3Code: e}
                    } as DoctorRequestDto)}
                    defaultValue={doctor?.address?.alpha3Code}
                    showSearch
                    placeholder={t("person.placeholder.country")}
                    optionFilterProp="children"
                    filterOption={(input, option) => {
                        return (option!.children![2] as unknown as string).toLowerCase().includes(input.toLowerCase())
                    }}
                    filterSort={(optionA, optionB) =>
                        (optionA!.children![2] as unknown as string)
                            .toLowerCase()
                            .localeCompare((optionB!.children![2] as unknown as string).toLowerCase())
                    }
                >
                    {countries.map(country => <Option key={country.alpha3Code}
                                                      value={country.alpha3Code}><img src={country.flag!}
                                                                                      alt={country.alpha3Code!}
                                                                                      width="20"></img> {country.name}
                    </Option>)}

                </Select>
            </Form.Item>
            <Form.Item label={t("person.field.address")}>
                <Input defaultValue={doctor?.address?.streetAddress1 as string | undefined}
                       placeholder={t("person.placeholder.address")} onChange={(e) => {
                    changeDoctor({
                        ...doctor,
                        address: {...doctor?.address, streetAddress1: e.target.value}
                    } as DoctorRequestDto)
                }}/>
            </Form.Item>
            <Form.Item label={t("person.field.address2")}>
                <Input defaultValue={doctor?.address?.streetAddress2 as string | undefined}
                       placeholder={t("person.placeholder.address2")} onChange={(e) => {
                    changeDoctor({
                        ...doctor,
                        address: {...doctor?.address, streetAddress2: e.target.value}
                    } as DoctorRequestDto)
                }}/>
            </Form.Item>
            <Form.Item label={t("person.field.city")}>
                <Input defaultValue={doctor?.address?.city as string | undefined} placeholder={t("person.placeholder.city")}
                       onChange={(e) => {
                           changeDoctor({
                               ...doctor,
                               address: {...doctor?.address, city: e.target.value}
                           } as DoctorRequestDto)
                       }}/>
            </Form.Item>
            <Form.Item label={t("person.field.state")}>
                <Input defaultValue={doctor?.address?.state as string | undefined} placeholder={t("person.placeholder.state")}
                       onChange={(e) => {
                           changeDoctor({
                               ...doctor,
                               address: {...doctor?.address, state: e.target.value}
                           } as DoctorRequestDto)
                       }}/>
            </Form.Item>
            <Form.Item label={t("person.field.postcode")}>
                <Input defaultValue={doctor?.address?.postCode as string | undefined} placeholder={t("person.placeholder.postcode")}
                       onChange={(e) => {
                           changeDoctor({
                               ...doctor,
                               address: {...doctor?.address, postCode: e.target.value}
                           } as DoctorRequestDto)
                       }}/>
            </Form.Item>
        </Form>
    </div>


    const selectPersons = <SelectPersons columns={columns} showCaretakers={true}
                                         caretakers={doctorCaretakers?.map((e) => {
                                             return {
                                                 id: e.id,
                                                 firstName: e.firstName,
                                                 lastName: e.lastName,
                                                 phoneNumber: e.userAccount?.mobileNumber,
                                                 email: e.userAccount?.emailAddress
                                             } as Person;
                                         }) ?? []}

                                         onCaretakerReload={reloadDoctorCaretakers}

                                         onCaretakersRemove={onCaretakersRemove}

                                         onCaretakersAssign={onCaretakersAssign}


                                         patients={doctorPatients?.map((e) => {
                                             return {
                                                 id: e.id,
                                                 firstName: e.firstName,
                                                 lastName: e.lastName,
                                                 phoneNumber: e.userAccount?.mobileNumber,
                                                 email: e.userAccount?.emailAddress
                                             } as Person;
                                         }) ?? []}

                                         onPatientsReload={reloadDoctorPatients}

                                         onPatientsRemove={onPatientsRemove}

                                         onPatientsAssign={onPatientsAssign}

                                         personTitle={t("tabs.doctor")}
                                         person={editDoctor}
    />

    return (
        <>
            <PageHeader
                eyebrow={t("nav.doctors")}
                title={isNew ? t("doctor.new") : (doctorName || t("doctor.one"))}
                actions={
                    <Button icon={<ArrowLeftOutlined/>} onClick={onCancel}>{t("common.back")}</Button>
                }
            />
            {selectPersons}
            <ConfirmDialog
                visible={isRemoveDoctorWarningVisible}
                tone="warning"
                title={t("confirm.remove.title")}
                body={t("confirm.remove.doctor", { name: doctorName })}
                confirmLabel={t("common.remove")}
                onConfirm={onRemoveDoctor}
                onCancel={() => setIsRemoveDoctorWarningVisible(false)}
            />
            <ConfirmDialog
                visible={isDeleteDoctorWarningVisible}
                title={t("confirm.delete.title")}
                body={t("confirm.delete.doctor", { name: doctorName })}
                confirmLabel={t("common.delete")}
                onConfirm={onDeleteDoctor}
                onCancel={() => setIsDeleteDoctorWarningVisible(false)}
            />
        </>
    );
}
